import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Copy,
  Download,
  Trash2,
  Pencil,
  FileText,
  LayoutDashboard,
  Briefcase,
  ArrowLeftRight,
  Wallet,
  TrendingUp,
  Receipt,
  PieChart,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import TopBar from "../components/TopBar";
import TabNav from "../components/TabNav";
import OverviewSection from "../components/OverviewSection";
import HoldingsTable from "../components/HoldingsTable";
import PerformanceChart from "../components/PerformanceChart";
import TransactionDialog from "../components/TransactionDialog";
import CapitalSection from "../components/CapitalSection";
import FeesSection from "../components/FeesSection";
import DiversificationSection from "../components/DiversificationSection";
import EditAvgCostDialog from "../components/EditAvgCostDialog";
import { apiClient, API } from "../lib/api";
import { getToken } from "../lib/auth";
import { fmtInr, fmtPct, pnlClass } from "../lib/format";

function StatCard({ label, value, sub, valueClass = "", onClick, testId }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      data-testid={testId}
      className={`text-left bg-card p-6 border border-subtle group ${
        onClick ? "hover:border-lime/50 cursor-pointer transition-colors" : ""
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] text-muted-foreground uppercase-track">{label}</div>
        {onClick ? (
          <ChevronRight
            className="h-3.5 w-3.5 text-muted-foreground/60 group-hover:text-lime"
            strokeWidth={1.5}
          />
        ) : null}
      </div>
      <div className={`text-3xl font-semibold mt-2 tabular ${valueClass}`}>{value}</div>
      {sub ? <div className="text-xs text-muted-foreground mt-1.5">{sub}</div> : null}
    </Tag>
  );
}

function TransactionsTable({ items, onDelete, onEdit, isAdvisor }) {
  if (!items || items.length === 0) {
    return (
      <div className="border border-subtle p-10 text-center text-sm text-muted-foreground bg-card">
        No transactions yet.
      </div>
    );
  }
  return (
    <div className="border border-subtle bg-card overflow-x-auto">
      <table className="w-full text-sm" data-testid="transactions-table">
        <thead className="bg-secondary border-b border-subtle">
          <tr className="text-[10px] uppercase-track text-muted-foreground">
            <th className="text-left p-3 font-medium">Date</th>
            <th className="text-left p-3 font-medium">Type</th>
            <th className="text-left p-3 font-medium">Symbol</th>
            <th className="text-right p-3 font-medium">Qty</th>
            <th className="text-right p-3 font-medium">Price</th>
            <th className="text-right p-3 font-medium">Value</th>
            {isAdvisor ? <th className="p-3" /> : null}
          </tr>
        </thead>
        <tbody>
          {items.map((tx) => (
            <tr key={tx.id} data-testid={`tx-row-${tx.id}`} className="border-b border-subtle hover:bg-secondary">
              <td className="p-3 font-mono tabular text-xs">{tx.tx_date}</td>
              <td className="p-3">
                <span
                  className={`text-[10px] px-2 py-0.5 font-semibold uppercase-track ${
                    tx.tx_type === "BUY" ? "bg-profit-soft" : "bg-loss-soft"
                  }`}
                >
                  {tx.tx_type}
                </span>
              </td>
              <td className="p-3 font-mono text-sm">{tx.symbol}</td>
              <td className="p-3 text-right tabular">{Number(tx.quantity).toLocaleString("en-IN")}</td>
              <td className="p-3 text-right tabular">
                ₹{Number(tx.price).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
              </td>
              <td className="p-3 text-right tabular">
                ₹{(Number(tx.quantity) * Number(tx.price)).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
              </td>
              {isAdvisor ? (
                <td className="p-3 text-right">
                  <div className="inline-flex items-center gap-3 justify-end">
                    <button
                      data-testid={`tx-edit-${tx.id}`}
                      onClick={() => onEdit(tx)}
                      className="text-muted-foreground/70 hover:text-foreground"
                      title="Edit transaction"
                    >
                      <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </button>
                    <button
                      data-testid={`tx-delete-${tx.id}`}
                      onClick={() => onDelete(tx.id)}
                      className="text-muted-foreground/70 hover:text-loss"
                      title="Delete transaction"
                    >
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </button>
                  </div>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdvisorClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [perf, setPerf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [editingTx, setEditingTx] = useState(null);
  const [editingAvgCost, setEditingAvgCost] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, pr] = await Promise.all([
        apiClient.get(`/advisor/clients/${id}/portfolio`),
        apiClient.get(`/advisor/clients/${id}/performance`),
      ]);
      setData(p.data);
      setPerf(pr.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const deleteTx = async (txId) => {
    if (!window.confirm("Delete this transaction?")) return;
    try {
      await apiClient.delete(`/advisor/clients/${id}/transactions/${txId}`);
      toast.success("Transaction deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const deleteClient = async () => {
    try {
      await apiClient.delete(`/advisor/clients/${id}`);
      toast.success("Client deleted");
      nav("/advisor", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await fetch(`${API}/advisor/clients/${id}/report.pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${data?.client?.name || "portfolio"}_report.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download PDF");
    }
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-background">
        <TopBar subtitle="Advisor Console" />
        <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  const t = data.totals;
  const c = data.client;
  const totalPnl = (t.unrealized_pnl || 0) + (data.realized_pnl || 0);

  const tabs = [
    { id: "overview", label: "Overview", icon: <LayoutDashboard className="h-3.5 w-3.5" strokeWidth={1.5} /> },
    { id: "holdings", label: "Holdings", icon: <Briefcase className="h-3.5 w-3.5" strokeWidth={1.5} />, count: data.holdings.length },
    { id: "diversification", label: "Diversification", icon: <PieChart className="h-3.5 w-3.5" strokeWidth={1.5} /> },
    { id: "transactions", label: "Transactions", icon: <ArrowLeftRight className="h-3.5 w-3.5" strokeWidth={1.5} />, count: data.transactions.length },
    { id: "capital", label: "Capital", icon: <Wallet className="h-3.5 w-3.5" strokeWidth={1.5} /> },
    { id: "performance", label: "Performance", icon: <TrendingUp className="h-3.5 w-3.5" strokeWidth={1.5} /> },
    { id: "fees", label: "Fees", icon: <Receipt className="h-3.5 w-3.5" strokeWidth={1.5} /> },
  ];

  return (
    <div className="min-h-screen bg-background">
      <TopBar subtitle="Advisor Console" />
      <main className="px-4 sm:px-6 lg:px-10 py-4 sm:py-6">
        <Link
          to="/advisor"
          data-testid="back-to-clients"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-lime mb-4 font-mono uppercase tracking-[0.2em]"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
          All clients
        </Link>

        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
          <div>
            <div className="text-[11px] text-muted-foreground uppercase-track">Client portfolio</div>
            <h1 className="text-3xl font-semibold mt-1 tracking-tight">{c.name}</h1>
            <div className="text-sm text-muted-foreground mt-2 flex flex-wrap items-center gap-3">
              {c.email ? <span className="font-mono">{c.email}</span> : null}
              <span className="inline-flex items-center gap-1.5">
                <span className="text-[10px] uppercase-track text-muted-foreground/70">Code</span>
                <code className="px-1.5 py-0.5 bg-gold-soft text-xs font-mono">{c.client_code}</code>
                <button
                  data-testid="copy-client-code"
                  onClick={() => {
                    navigator.clipboard?.writeText(c.client_code);
                    toast.success("Code copied");
                  }}
                  className="text-muted-foreground/60 hover:text-lime"
                  title="Copy code"
                >
                  <Copy className="h-3 w-3" strokeWidth={1.5} />
                </button>
              </span>
              <span>
                Started <span className="font-mono">{c.start_date}</span>
              </span>
              {c.annual_fee_pct ? (
                <span>
                  Fee <span className="font-mono">{c.annual_fee_pct}%</span> p.a.
                </span>
              ) : null}
            </div>
          </div>
          <div className="flex items-center gap-2 self-start">
            <TransactionDialog clientId={id} onCreated={load} />
            <button
              data-testid="download-pdf-btn"
              onClick={downloadPdf}
              className="border border-border hover:bg-secondary px-2.5 sm:px-3 py-2 text-xs font-semibold uppercase-track flex items-center gap-1.5"
              title="Download statement"
            >
              <Download className="h-3.5 w-3.5" strokeWidth={1.5} />
              <span className="hidden sm:inline">PDF</span>
            </button>
            <button
              data-testid="delete-client-btn"
              onClick={() => setConfirmDelete(true)}
              className="border border-[hsl(var(--loss)/0.4)] text-loss hover:bg-[hsl(var(--loss)/0.1)] px-2.5 sm:px-3 py-2 text-xs font-bold uppercase tracking-[0.2em] rounded-md flex items-center gap-1.5"
              title="Delete client"
            >
              <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
              <span className="hidden sm:inline">Delete</span>
            </button>
          </div>
        </div>

        <TabNav tabs={tabs} active={activeTab} onChange={setActiveTab} />

        <div className="mt-6">
          {activeTab === "overview" && (
            <div data-testid="tab-content-overview">
              <OverviewSection data={data} perf={perf} onJump={setActiveTab} />
            </div>
          )}

          {activeTab === "holdings" && (
            <section data-testid="tab-content-holdings">
              <HoldingsTable
                holdings={data.holdings}
                onEditAvgCost={(h) => setEditingAvgCost(h)}
              />
            </section>
          )}

          {activeTab === "diversification" && (
            <section data-testid="tab-content-diversification">
              <DiversificationSection scope="advisor" clientId={id} />
            </section>
          )}

          {activeTab === "transactions" && (
            <section data-testid="tab-content-transactions">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs text-muted-foreground">
                  {data.transactions.length} entries
                </div>
                <TransactionDialog clientId={id} onCreated={load} />
              </div>
              <TransactionsTable items={data.transactions} onDelete={deleteTx} onEdit={setEditingTx} isAdvisor />
            </section>
          )}

          {activeTab === "capital" && (
            <section data-testid="tab-content-capital">
              <CapitalSection
                clientId={id}
                initialCapital={c.initial_capital}
                startDate={c.start_date}
                onChange={load}
              />
            </section>
          )}

          {activeTab === "performance" && (
            <section className="space-y-5" data-testid="tab-content-performance">
              {perf?.summary ? (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
                  {[
                    ["Portfolio", perf.summary.portfolio_return],
                    ["NIFTY 50", perf.summary.nifty50_return],
                    ["SENSEX", perf.summary.sensex_return],
                    ["NIFTY 500", perf.summary.nifty500_return],
                  ].map(([label, val]) => (
                    <div key={label} className="bg-card p-4">
                      <div className="text-[10px] text-muted-foreground uppercase-track">{label}</div>
                      <div className={`text-xl font-semibold mt-1 tabular ${pnlClass(val)}`}>
                        {fmtPct(val)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
              <PerformanceChart series={perf?.series || []} />
              <div className="text-xs text-muted-foreground">Since {c.start_date}</div>
            </section>
          )}

          {activeTab === "fees" && (
            <section data-testid="tab-content-fees">
              <FeesSection clientId={id} scope="advisor" refreshKey={data.transactions.length} />
            </section>
          )}
        </div>
      </main>

      <TransactionDialog
        clientId={id}
        transaction={editingTx}
        open={!!editingTx}
        onOpenChange={(v) => { if (!v) setEditingTx(null); }}
        onCreated={() => { setEditingTx(null); load(); }}
      />

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-subtle p-6 max-w-sm w-full">
            <div className="text-base font-semibold mb-2">Delete client?</div>
            <p className="text-sm text-muted-foreground mb-5">
              This permanently removes {c.name} and all their transactions.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(false)}
                className="border border-border px-3 py-2 text-xs uppercase-track"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-delete-client"
                onClick={deleteClient}
                className="bg-loss hover:brightness-110 text-white px-3 py-2 text-xs uppercase tracking-[0.2em] font-bold rounded-md active:scale-[0.98]"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <EditAvgCostDialog
        clientId={id}
        holding={editingAvgCost}
        open={!!editingAvgCost}
        onOpenChange={(v) => { if (!v) setEditingAvgCost(null); }}
        onSaved={load}
      />
    </div>
  );
}
